import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Badge, Button, Typography } from 'components';
import { HubspotIntegrationTypeEnum, IntegrationEnum } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { CheckCircleIcon, DotsVerticalIcon, PlusIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useHubSpotIntegration } from 'providers/HubSpotIntegrationProviders/useHubSpotIntegration';
import { useHubspotIntegrationDestroy } from 'providers/HubSpotIntegrationProviders/useHubspotIntegrationDestroy';
import { FC, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import ModalHubSpotDisconnect from './ModalHubSpotDisconnect';

interface IHubSpotConnect {
  appClientId?: string;
  viewLink: string;
  scope: string;
  type: HubspotIntegrationTypeEnum;
}

const HubSpotConnect: FC<IHubSpotConnect> = ({ appClientId, viewLink, scope, type }) => {
  const t = useTranslations();
  const router = useRouter();

  const remapExternal = {
    [HubspotIntegrationTypeEnum.Hubdb]: IntegrationEnum.Hubdb,
    [HubspotIntegrationTypeEnum.Content]: IntegrationEnum.Hubspot
  };

  const scopeEncoded = encodeURIComponent(scope);
  const redirectUriFull = `${process.env.NEXT_PUBLIC_REDIRECT_HOST}/external/${remapExternal[type]}/callback`;

  const [openModalDisconnect, setOpenModalDisconnect] = useState(false);

  const { data, refetch } = useHubSpotIntegration({ type });

  const handleOpenModalDisconnect = () => setOpenModalDisconnect(true);
  const handleCloseModalDisconnect = () => setOpenModalDisconnect(false);

  const { mutateAsync: hubspotIntegrationDestroy } = useHubspotIntegrationDestroy();
  const handleDisconnect = async () => {
    try {
      await hubspotIntegrationDestroy({ type });
      await refetch();
      router.push('/integrations');
      toaster.success(t('messages.success.disconnect_hubspot'));
      handleCloseModalDisconnect();
    } catch (error) {
      toaster.error(t('messages.error.disconnect_hubspot'));
      notifyException(error, ESpansException.IntegrationHubSpotException);
      handleCloseModalDisconnect();
    }
  };

  return (
    <>
      <ModalHubSpotDisconnect
        open={openModalDisconnect}
        onCancel={handleCloseModalDisconnect}
        onClick={handleDisconnect}
      />
      {data?.hubspotIntegration?.id && (
        <div className="absolute right-4 top-1 ">
          <div className="flex h-5 w-6 flex-col items-end justify-end pt-3">
            <Menu>
              <MenuButton className="absolute !m-0 h-4 w-4 !outline-1 !outline-offset-0 outline-primary-blue-200">
                <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('buttons.options')}>
                  <DotsVerticalIcon className="rotate-90 transition-all hover:fill-tertiary-100" />
                </div>
              </MenuButton>
              <Transition
                enter="transition ease-out duration-75"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <MenuItems
                  anchor="bottom end"
                  className="w-36 origin-top-right rounded-md bg-white p-1 text-sm/6 text-red-500 shadow-md [--anchor-gap:var(--spacing-1)] focus:outline-none"
                >
                  <MenuItem>
                    <button
                      className="flex w-full items-center gap-1 rounded-3xl p-1.5 data-[focus]:bg-primary-blue-900"
                      onClick={handleOpenModalDisconnect}
                    >
                      <Typography variant="medium5" className="!mt-0.5 text-secondary-red ">
                        {t('buttons.disconnect')}
                      </Typography>
                    </button>
                  </MenuItem>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      )}
      {!data?.hubspotIntegration?.id ? (
        <Link
          className="mt-6 w-full"
          href={`https://app-eu1.hubspot.com/oauth/authorize?client_id=${appClientId}&redirect_uri=${redirectUriFull}&scope=${scopeEncoded}`}
        >
          <Button
            colorIcon="white"
            color="secondary"
            size="small"
            className="w-full"
            startAdornment={<PlusIcon className="scale-125" />}
            label={t('integrations.connect')}
          />
        </Link>
      ) : (
        <div className="flex w-full flex-col items-center">
          <Badge
            icon={<CheckCircleIcon className="scale-[1]" />}
            className="relative mb-4 h-6 !bg-primary-green-300"
            classNameTypography="text-primary-green-100 ml-1 !text-xs"
          >
            {t('labels.connected')}
          </Badge>
          <Link href={viewLink} className="w-full">
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              className="w-full"
              label={t('buttons.view')}
            />
          </Link>
        </div>
      )}
    </>
  );
};

export default HubSpotConnect;
